import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { motion, useReducedMotion } from 'framer-motion';
import { FiArrowUpRight } from 'react-icons/fi';
import styled from 'styled-components';
import PrivacyModal from './PrivacyModal';
import TermsModal from './TermsModal';

const FooterWrapper = styled.footer`
  position: relative;
  overflow: hidden;
  padding: 72px 0 36px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background:
    linear-gradient(rgba(129, 155, 190, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(129, 155, 190, 0.035) 1px, transparent 1px),
    #0d1119;
  background-size: 64px 64px, 64px 64px, auto;
  color: #f7f9fc;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 84% 0%, rgba(65, 130, 205, 0.12), transparent 30%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.018), transparent 42%);
    pointer-events: none;
  }

  @media (max-width: 620px) {
    padding: 58px 0 30px;
    background-size: 48px 48px, 48px 48px, auto;
  }
`;

const FooterReveal = styled(motion.div)`
  position: relative;
  z-index: 1;
`;

const FooterTop = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(300px, 0.8fr);
  gap: 52px;
  align-items: end;
  padding-bottom: 46px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 34px;
  }
`;

const Company = styled.div`
  img {
    display: block;
    width: auto;
    height: 19px;
    margin-bottom: 25px;
    opacity: 0.9;
  }

  p {
    margin: 5px 0 0;
    color: #7f8b99;
    font-size: 0.75rem;
    font-weight: 510;
    line-height: 1.55;
    letter-spacing: -0.01em;
  }
`;

const FooterAside = styled.div`
  justify-self: end;
  max-width: 420px;

  .kicker {
    color: #7ebdff;
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 10px;
    color: #f7f9fc;
    font-size: clamp(1.35rem, 2.5vw, 2rem);
    font-weight: 760;
    line-height: 1.25;
    letter-spacing: -0.038em;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
    color: #a9b8c8;
    font-size: 0.8rem;
    font-weight: 690;
    transition: color 180ms ease;
  }

  a svg {
    transition: transform 220ms ease;
  }

  a:hover,
  a:focus-visible {
    color: #fff;
  }

  a:hover svg,
  a:focus-visible svg {
    transform: translate(3px, -3px);
  }

  @media (max-width: 760px) {
    justify-self: start;
  }

  @media (hover: none) and (pointer: coarse), (prefers-reduced-motion: reduce) {
    a:hover svg,
    a:focus-visible svg {
      transform: none;
    }
  }
`;

const FooterBottom = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  p {
    margin: 0;
    color: #616d7a;
    font-size: 0.7rem;
    font-weight: 540;
  }

  @media (max-width: 620px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;
  }
`;

const LinkContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 22px;
`;

const PolicyButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: #8d99a7;
  font-size: 0.74rem;
  font-weight: 650;
  transition: color 180ms ease;

  &:hover,
  &:focus-visible {
    color: #fff;
    outline: none;
  }
`;

const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const canObserveViewport = typeof window !== 'undefined' && 'IntersectionObserver' in window;

  return (
    <FooterWrapper>
      <Container>
        <FooterReveal
          initial={prefersReducedMotion || !canObserveViewport ? false : { opacity: 0, y: 48 }}
          animate={!canObserveViewport ? { opacity: 1, y: 0 } : undefined}
          whileInView={canObserveViewport ? { opacity: 1, y: 0 } : undefined}
          viewport={canObserveViewport ? { once: true, amount: 0.35 } : undefined}
          transition={{ duration: 0.82, ease: [0.22, 1, 0.36, 1] }}
        >
          <FooterTop>
            <Company>
              <img src={`${process.env.PUBLIC_URL}/naniaraeb_white.png`} alt="NARNIA LAB" />
              <p>(주)나니아랩 대표자: 김찬석, 김홍일 | 사업자등록번호: 201-86-48902</p>
              <p>통신판매업신고번호: 2026-울산북구-0079 | 전화: 010-4027-9445</p>
              <p>주소: 울산광역시 북구 농공단지1길 32-8, 북구청년창업지원센터</p>
            </Company>
            <FooterAside>
              <span className="kicker">From question to live</span>
              <strong>아이의 질문이<br />작동하는 프로젝트가 됩니다.</strong>
              <a href="https://narnia-lab.com/">
                나니아랩 홈페이지 보기 <FiArrowUpRight aria-hidden="true" />
              </a>
            </FooterAside>
          </FooterTop>
          <FooterBottom>
            <p>© 2025 Narnia Lab. All rights reserved.</p>
            <LinkContainer>
              <PolicyButton type="button" onClick={() => setShowPrivacy(true)}>개인정보 처리방침</PolicyButton>
              <PolicyButton type="button" onClick={() => setShowTerms(true)}>이용약관</PolicyButton>
            </LinkContainer>
          </FooterBottom>
        </FooterReveal>
      </Container>

      <PrivacyModal show={showPrivacy} onHide={() => setShowPrivacy(false)} />
      <TermsModal show={showTerms} onHide={() => setShowTerms(false)} />
    </FooterWrapper>
  );
};

export default Footer;
